import {AceLoggerService} from '@logging/ace-logger.service';
import {AceLogger} from '@logging/ace-logger';
import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {BaseService} from '../base-service';
import {NetworkProvider, NetworkProviderPlan, NetworkProviderPlanEntry, NetworkProviderPlanPeriodType} from '../access-sites.interface';
import {PagedModel} from '@shared/api/model/paged-model';
import {ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot} from '@angular/router';
import moment, {Moment} from 'moment-timezone';
import {APIResponse} from '@app/layout/admin/services/api/api.interface';
import {GQLPagingParamsVariableWithoutNoCount, GRAPHQL_TYPES, GraphQlVariables, HttpUtilityService} from '@shared/services/api/http-utility-service';
import {StringUtils} from '@shared/util/general/string-utils';
import Fields from "gql-query-builder/build/Fields";
import * as gql from 'gql-query-builder';
import {ClassTransformer, Transform, Type} from "class-transformer";
import {CollectionUtils} from "@shared/util/collections/collection-utils";
import {NetworkProviderId, NetworkProviderTypeId} from "@shared/services";
import {AppConstants} from "@shared/util/app-constants";

interface ProviderPlanAPIDelete {
  network_provider_plan_id: number;
  deleted: boolean;
}

enum NETWORK_PROVIDER_PLAN_OPERATIONS {
  PLAN = 'network_provider_plan',
  PLANS = 'network_provider_plans'
}

export const networkProviderPlansResolver: ResolveFn<NetworkProviderPlan> = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<NetworkProviderPlan> => {
  return inject(NetworkProviderPlansService).getNetworkProviderPlan(StringUtils.stringToNumber(route.paramMap.get('id')));
};

export type NetworkProviderPlanId = number;

export const networkProviderPlanFields: Fields = [
  'network_provider_plan_id',
  'name',
  'use_site_timezone',
  'timezone',
  {
    entries: [
      'start_month',
      'end_month',
      'start_day',
      'end_day',
      'start_time',
      'end_time',
      'network_provider_plan_period_type_id',
      {
        type: ['network_provider_plan_period_type_id', 'name']
      }
    ]
  },
  'network_provider_id',
  {
    network_provider: [
      'network_provider_id',
      'name',
      'shared',
      'customer_id',
      'network_provider_type_id',
      {
        network_provider_type: [
          'network_provider_type_id',
          'network_provider_type_name'
        ]
      }
    ]
  }
];


const networkProviderPlansVariables: GraphQlVariables = {
  network_provider_plan_id: {type: GRAPHQL_TYPES.INTEGER},
  name: {type: GRAPHQL_TYPES.STRING},
  network_provider_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  network_provider_type_ids: {type: GRAPHQL_TYPES.INTEGER, list: true},
  ...GQLPagingParamsVariableWithoutNoCount
};


export interface NetworkProviderPlansSearchTerm {
  network_provider_plan_id?: NetworkProviderPlanId;
  name?: string;
  network_provider_ids?: NetworkProviderId[];
  network_provider_type_ids?: NetworkProviderTypeId[];
}


class NetworkProviderPlanEntryImpl implements NetworkProviderPlanEntry {
  start_month: number;
  end_month: number;
  start_day: number;
  end_day: number;

  @Type(() => moment)
  @Transform(({value}: {value: string | null}) => value ? moment.utc(value, AppConstants.TIMESTAMP_TIME_FORMAT) : value, {toClassOnly: true})
  @Transform(({value}: {value: Moment | null}) => value ? value.format(AppConstants.TIMESTAMP_TIME_FORMAT) : value, {toPlainOnly: true})
  start_time: moment.Moment;

  @Type(() => moment)
  @Transform(({value}: {value: string | null}) => value ? moment.utc(value, AppConstants.TIMESTAMP_TIME_FORMAT) : value, {toClassOnly: true})
  @Transform(({value}: {value: Moment | null}) => value ? value.format(AppConstants.TIMESTAMP_TIME_FORMAT) : value, {toPlainOnly: true})
  end_time: moment.Moment;

  network_provider_plan_period_type_id: number;
  type?: NetworkProviderPlanPeriodType;
}


export class NetworkProviderPlanImpl implements NetworkProviderPlan {
  network_provider_plan_id?: number;
  name: string;
  network_provider_id: number;
  network_provider: NetworkProvider;
  use_site_timezone: boolean;
  timezone: string;
  @Type(() => NetworkProviderPlanEntryImpl)
  entries: NetworkProviderPlanEntry[];
}


@Injectable({
  providedIn: 'root'
})
export class NetworkProviderPlansService extends BaseService {

  private readonly URL_PART = '/network-providers/plans';
  private readonly logger: AceLogger;

  constructor(private http: HttpClient,
              private httpUtilityService: HttpUtilityService,
              private classTransformer: ClassTransformer,
              logService: AceLoggerService) {
    super();
    this.logger = logService.create(NetworkProviderPlansService.name);
  }

  getNetworkProviderPlans(searchTerm: NetworkProviderPlansSearchTerm | null, startIndex: number, limit: number): Observable<PagedModel<NetworkProviderPlan[]>> {
    const operation = NETWORK_PROVIDER_PLAN_OPERATIONS.PLANS.valueOf();
    const q = gql.query({
      operation,
      fields: networkProviderPlanFields,
      variables: networkProviderPlansVariables
    });
    return this.httpUtilityService.graphQL<NetworkProviderPlan[]>(q, operation, {...searchTerm, start_index: startIndex, limit}).pipe(map(result => ({
      ...result,
      data: this.classTransformer.plainToInstance(NetworkProviderPlanImpl, result.data)
    })));
  }

  public getNetworkProviderPlan(networkProviderPlanId: number): Observable<NetworkProviderPlan> {
    return this.getNetworkProviderPlans({network_provider_plan_id: networkProviderPlanId}, 0, 1).pipe(map(plans => CollectionUtils.only(plans.data)));
  }

  public createNetworkProviderPlan(networkProviderPlan: NetworkProviderPlan): Observable<NetworkProviderPlan> {
    const np = this.classTransformer.instanceToPlain(networkProviderPlan);
    const options = {headers: this.getHeaders()};
    return this.http.post(this.getGateway() + this.URL_PART, np, options)
      .pipe(map((response: APIResponse<NetworkProviderPlan>) => this.httpUtilityService.extractResponseData(response, NETWORK_PROVIDER_PLAN_OPERATIONS.PLAN)));
  }

  public updateNetworkProviderPlan(networkProviderPlan: NetworkProviderPlan): Observable<NetworkProviderPlan> {
    const np = this.classTransformer.instanceToPlain(networkProviderPlan);
    const options = {headers: this.getHeaders()};
    return this.http.put(this.getGateway() + this.URL_PART + '/' + np.network_provider_plan_id, np, options)
      .pipe(map((response: APIResponse<NetworkProviderPlan>) => this.httpUtilityService.extractResponseData(response, NETWORK_PROVIDER_PLAN_OPERATIONS.PLAN)));
  }

  public deleteNetworkProviderPlan(providerPlanId: number): Observable<number> {
    const options = {headers: this.getHeaders()};
    return this.http.delete(this.getGateway() + this.URL_PART + '/' + providerPlanId, options)
      .pipe(map((response: APIResponse<ProviderPlanAPIDelete>) => {
        return this.httpUtilityService.extractResponseData<ProviderPlanAPIDelete>(response, NETWORK_PROVIDER_PLAN_OPERATIONS.PLAN).network_provider_plan_id;
      }));
  }
}
